import { COLORS, type Color } from "./palette.js";

/**
 * Tiny indexed bitmap with an origin, used to compose sprites as data.
 * Coordinates are relative to the origin (for characters: feet centre on the ground).
 */
export class Bitmap {
  readonly data: (Color | null)[];
  constructor(
    readonly w: number,
    readonly h: number,
    readonly ox = 0,
    readonly oy = 0,
  ) {
    this.data = new Array<Color | null>(w * h).fill(null);
  }

  get(x: number, y: number): Color | null {
    const px = x + this.ox;
    const py = y + this.oy;
    if (px < 0 || py < 0 || px >= this.w || py >= this.h) return null;
    return this.data[py * this.w + px] ?? null;
  }

  set(x: number, y: number, c: Color | null): void {
    const px = Math.round(x) + this.ox;
    const py = Math.round(y) + this.oy;
    if (px < 0 || py < 0 || px >= this.w || py >= this.h) return;
    this.data[py * this.w + px] = c;
  }

  rect(x: number, y: number, w: number, h: number, c: Color | null): void {
    for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) this.set(x + i, y + j, c);
  }

  /** Thick line (square brush), Bresenham. */
  line(x0: number, y0: number, x1: number, y1: number, thickness: number, c: Color): void {
    x0 = Math.round(x0);
    y0 = Math.round(y0);
    x1 = Math.round(x1);
    y1 = Math.round(y1);
    const dx = Math.abs(x1 - x0);
    const dy = -Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx + dy;
    const off = Math.floor((thickness - 1) / 2);
    for (let guard = 0; guard < 200; guard++) {
      this.rect(x0 - off, y0 - off, thickness, thickness, c);
      if (x0 === x1 && y0 === y1) break;
      const e2 = 2 * err;
      if (e2 >= dy) {
        err += dy;
        x0 += sx;
      }
      if (e2 <= dx) {
        err += dx;
        y0 += sy;
      }
    }
  }

  /** Paint another bitmap (or sprite) at x,y relative to this origin. */
  blit(src: Bitmap, x: number, y: number, flip = false): void {
    for (let j = 0; j < src.h; j++)
      for (let i = 0; i < src.w; i++) {
        const c = src.data[j * src.w + i];
        if (!c) continue;
        const sx = flip ? src.w - 1 - i : i;
        this.set(x + sx - src.ox, y + j - src.oy, c);
      }
  }

  /** 1px outline around opaque pixels (pixel-art readability on big screens). */
  outline(c: Color = COLORS.ink): void {
    const add: number[] = [];
    for (let y = 0; y < this.h; y++)
      for (let x = 0; x < this.w; x++) {
        if (this.data[y * this.w + x]) continue;
        const n =
          (x > 0 && this.data[y * this.w + x - 1]) ||
          (x < this.w - 1 && this.data[y * this.w + x + 1]) ||
          (y > 0 && this.data[(y - 1) * this.w + x]) ||
          (y < this.h - 1 && this.data[(y + 1) * this.w + x]);
        if (n) add.push(y * this.w + x);
      }
    for (const i of add) this.data[i] = c;
  }

  /** Run-length rects per row: [x, y, w, h, color] relative to the origin. */
  toRects(): [number, number, number, number, Color][] {
    const out: [number, number, number, number, Color][] = [];
    for (let y = 0; y < this.h; y++) {
      let x = 0;
      while (x < this.w) {
        const c = this.data[y * this.w + x];
        if (!c) {
          x++;
          continue;
        }
        let run = 1;
        while (x + run < this.w && this.data[y * this.w + x + run] === c) run++;
        out.push([x - this.ox, y - this.oy, run, 1, c]);
        x += run;
      }
    }
    return out;
  }

  colors(): Set<Color> {
    const s = new Set<Color>();
    for (const c of this.data) if (c) s.add(c);
    return s;
  }
}

/** Shared legend for character-grid sprites. '.' and ' ' are transparent. */
export const LEGEND: Record<string, Color> = {
  k: COLORS.ink,
  n: COLORS.night,
  w: COLORS.floor,
  W: COLORS.floorDark,
  l: COLORS.woodLight,
  L: COLORS.floorLight,
  c: COLORS.cream,
  p: COLORS.paper,
  h: COLORS.white,
  a: COLORS.greyLight,
  g: COLORS.grey,
  G: COLORS.greyDark,
  r: COLORS.red,
  R: COLORS.redDark,
  o: COLORS.orange,
  y: COLORS.yellow,
  Y: COLORS.yellowLight,
  e: COLORS.green,
  E: COLORS.greenDark,
  t: COLORS.teal,
  s: COLORS.skyDay,
  b: COLORS.blue,
  B: COLORS.blueDark,
  u: COLORS.purple,
  i: COLORS.pink,
  m: COLORS.brown,
  d: COLORS.dusk,
  v: COLORS.wall,
  V: COLORS.wallDark,
};

/** Parse a grid sprite into a bitmap. Origin defaults to bottom-left. */
export function gridSprite(
  rows: readonly string[],
  origin: "bottom-left" | "top-left" = "bottom-left",
): Bitmap {
  const h = rows.length;
  const w = Math.max(...rows.map((r) => r.length));
  const bm = new Bitmap(w, h, 0, origin === "bottom-left" ? h - 1 : 0);
  rows.forEach((row, y) => {
    for (let x = 0; x < row.length; x++) {
      const ch = row[x]!;
      if (ch === "." || ch === " ") continue;
      const c = LEGEND[ch];
      if (!c) throw new Error(`Unknown sprite char '${ch}'`);
      bm.data[y * w + x] = c;
    }
  });
  return bm;
}
